import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Wallet, Shield, Zap, Globe } from 'lucide-react';

function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.pexels.com/photos/5044378/pexels-photo-5044378.jpeg)',
            backgroundPosition: 'center 40%'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/95 via-emerald-800/90 to-emerald-700/85" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Wallet className="w-12 h-12 text-white" />
              <h1 className="text-4xl font-bold text-white">CaddiePay</h1>
            </div>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Cashless Caddie Payments Made Simple
            </h2>
            <p className="text-xl sm:text-2xl text-emerald-100 mb-12 max-w-2xl">
              The modern digital wallet solution for South African golf courses. Pay your caddies instantly, securely, and effortlessly.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => navigate('/register')} 
                className="btn-primary text-lg h-14 px-10"
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
              <Button 
                onClick={() => navigate('/login')} 
                className="btn-secondary text-lg h-14 px-10"
                data-testid="sign-in-btn"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why CaddiePay?</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Built specifically for the South African golf community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="wallet-card text-center" data-testid="feature-instant">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-emerald-900" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Instant Transfers</h3>
              <p className="text-slate-600">
                Send money to caddies instantly with just their phone number
              </p>
            </div>

            <div className="wallet-card text-center" data-testid="feature-secure">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Secure & Safe</h3>
              <p className="text-slate-600">
                Bank-grade security with multi-factor authentication
              </p>
            </div>

            <div className="wallet-card text-center" data-testid="feature-reference">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-8 h-8 text-emerald-900" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No Smartphone Needed</h3>
              <p className="text-slate-600">
                Caddies can withdraw cash using reference codes at retail partners
              </p>
            </div>

            <div className="wallet-card text-center" data-testid="feature-local">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">South African</h3>
              <p className="text-slate-600">
                Integrated with local payment gateways and banking partners
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Three simple steps to cashless caddie payments
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-900 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Sign Up</h3>
              <p className="text-slate-600 text-lg">
                Create your account as a golfer or caddie in minutes
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-amber-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Load Wallet</h3>
              <p className="text-slate-600 text-lg">
                Golfers top-up their wallet using Instant EFT or card
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-900 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Pay & Withdraw</h3>
              <p className="text-slate-600 text-lg">
                Send payments instantly or generate withdrawal codes for cash pickup
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-900 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Go Cashless?
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            Join South Africa's leading digital wallet for golf course payments
          </p>
          <Button 
            onClick={() => navigate('/register')} 
            className="btn-secondary text-lg h-14 px-10"
            data-testid="cta-register-btn"
          >
            Create Free Account
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">
            &copy; 2025 CaddiePay. Simplifying caddie payments across South Africa.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;